import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Button, Modal, Select, InputNumber, Input, Spin, Empty } from "antd";
import { toast } from "../toasts/toastStore";
import { apiGeneralInstance, authHeaderGeneral } from "../../redux/actions/general/general";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DESTINO_LABEL = { inventario: "Regresa al inventario", merma: "Merma (se descarta)" };
const RESOLUCION_LABEL = { dinero: "Reembolso en dinero", saldo_favor: "Saldo a favor del cliente", otro_producto: "Cambio por otro producto" };

export default function DevolucionesVenta({ venta, onChanged }) {
  const idVenta = venta.id_venta;
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState({ destinos: [], resoluciones: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchItems = useCallback(() => {
    apiGeneralInstance.get(`/devoluciones/venta/${idVenta}`, { headers: authHeaderGeneral() })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("No se pudieron cargar las devoluciones"))
      .finally(() => setLoading(false));
  }, [idVenta]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    apiGeneralInstance.get("/devoluciones/catalogos", { headers: authHeaderGeneral() })
      .then((r) => setCat(r.data || { destinos: [], resoluciones: [] }))
      .catch(() => {});
  }, []);

  const lineas = (venta.detalle || []).filter((d) => d.tipo_linea === "producto");
  const devueltoPorLinea = items.reduce((acc, d) => {
    acc[d.id_venta_detalle] = (acc[d.id_venta_detalle] || 0) + parseFloat(d.cantidad);
    return acc;
  }, {});
  const disponible = (d) => parseFloat(d.cantidad) - (devueltoPorLinea[d.id_venta_detalle] || 0);
  const lineaSel = lineas.find((d) => d.id_venta_detalle === form.id_venta_detalle);
  const resolucionNombre = cat.resoluciones.find((r) => r.id === form.id_resolucion_devolucion)?.nombre;

  const handleSave = async () => {
    if (!form.id_venta_detalle) return toast.warning("Selecciona el producto");
    if (!form.cantidad || form.cantidad <= 0) return toast.warning("Indica la cantidad");
    if (!form.id_destino_devolucion) return toast.warning("Indica que pasa con el producto");
    if (!form.id_resolucion_devolucion) return toast.warning("Indica como se resuelve");
    if (!form.motivo?.trim()) return toast.warning("El motivo es obligatorio");
    setSaving(true);
    try {
      const res = await apiGeneralInstance.post(`/devoluciones/venta/${idVenta}`, form, { headers: authHeaderGeneral() });
      setItems(res.data?.items || []);
      toast.success("Devolucion registrada");
      setOpen(false);
      setForm({});
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la devolucion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cd-card">
      <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="cd-card-icon-wrap"><i className="fa-solid fa-rotate-left"></i></div>
          <h2 className="cd-card-title">Devoluciones</h2>
          <span className="cd-actividad-count">{items.length}</span>
        </div>
        {!venta.cancelada && lineas.length > 0 && (
          <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => { setForm({}); setOpen(true); }}
            style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Nueva devolucion</Button>
        )}
      </div>

      {loading ? <Spin /> : items.length === 0 ? <Empty description="Sin devoluciones" /> : (
        <div>
          {items.map((d) => (
            <div key={d.id_devolucion} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
              <div>
                <span className="cd-field-label">Producto</span>
                <div className="cd-field-value">{d.item_descripcion || "—"} x {parseFloat(d.cantidad)}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{d.motivo}</div>
              </div>
              <div>
                <span className="cd-field-label">Monto</span>
                <div className="cd-field-value">{d.monto_reembolsado != null ? money(d.monto_reembolsado) : "—"}</div>
              </div>
              <div>
                <span className="cd-field-label">Resolucion</span>
                <div className="cd-field-value">{RESOLUCION_LABEL[d.resolucion] || d.resolucion}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {DESTINO_LABEL[d.destino] || d.destino}{d.id_venta_nueva ? ` · Venta nueva #${d.id_venta_nueva}` : ""}
                </div>
              </div>
              <div>
                <span className="cd-field-label">Autorizo</span>
                <div className="cd-field-value">{d.autorizo_nombre}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{dayjs(d.datetime).format("D MMM YYYY, HH:mm")}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal title="Nueva devolucion" open={open} onCancel={() => setOpen(false)} onOk={handleSave} confirmLoading={saving} okText="Registrar" cancelText="Cancelar" okButtonProps={{ style: { backgroundColor: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div className="cd-field-label">Producto devuelto</div>
            <Select style={{ width: "100%" }} placeholder="Selecciona el producto" value={form.id_venta_detalle}
              onChange={(v) => setForm({ ...form, id_venta_detalle: v, cantidad: undefined })}
              options={lineas.map((d) => ({ value: d.id_venta_detalle, disabled: disponible(d) <= 0, label: `${d.item_nombre || d.descripcion} (disponible: ${disponible(d)})` }))} />
          </div>
          <div>
            <div className="cd-field-label">Cantidad</div>
            <InputNumber style={{ width: "100%" }} min={0} max={lineaSel ? disponible(lineaSel) : undefined} value={form.cantidad} onChange={(v) => setForm({ ...form, cantidad: v })} />
          </div>
          <div>
            <div className="cd-field-label">Que pasa con el producto</div>
            <Select style={{ width: "100%" }} value={form.id_destino_devolucion} onChange={(v) => setForm({ ...form, id_destino_devolucion: v })}
              options={cat.destinos.map((x) => ({ value: x.id, label: DESTINO_LABEL[x.nombre] || x.nombre }))} />
          </div>
          <div>
            <div className="cd-field-label">Como se resuelve</div>
            <Select style={{ width: "100%" }} value={form.id_resolucion_devolucion} onChange={(v) => setForm({ ...form, id_resolucion_devolucion: v })}
              options={cat.resoluciones.map((x) => ({ value: x.id, label: RESOLUCION_LABEL[x.nombre] || x.nombre }))} />
            {resolucionNombre === "saldo_favor" && !venta.id_cliente && (
              <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>Esta venta no tiene cliente: no se puede dar saldo a favor.</div>
            )}
          </div>
          {resolucionNombre === "otro_producto" && (
            <div>
              <div className="cd-field-label">Numero de la venta del producto nuevo (opcional)</div>
              <InputNumber style={{ width: "100%" }} min={1} value={form.id_venta_nueva} onChange={(v) => setForm({ ...form, id_venta_nueva: v })} />
            </div>
          )}
          <div>
            <div className="cd-field-label">Motivo</div>
            <Input.TextArea rows={2} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
